import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { OnboardingState } from './state';
import { OnboardingCommand } from './commands';

/**
 * Invariants enforce:
 * 1. Permissions - who can execute which commands (canApply)
 * 2. Normalization - state consistency (no-op stub for now)
 * 3. Validation - form validation for UI feedback (no-op stub for now)
 *
 * The three user-source "start" triggers are privileged: the submit/complete
 * policies react to them by calling backend.commands.*. They are therefore
 * capability-gated via `commandOperations` -> `gateCommandByOperation`, exactly
 * like the account/invoicing domains, so the front-end does not surface a
 * trigger the user lacks the capability for. The remaining commands are local
 * UI state and pass through the plain per-source allow-list.
 */

/**
 * Maps each privileged "start" trigger to the backend command its policy calls.
 * Capability is derived from the named client method (route-auth config), so the
 * UI gate stays in lock-step with the server.
 */
export const commandOperations: CommandOperationMap<OnboardingState, OnboardingCommand> = {
  'onboarding/invoicing/submit/start': 'ProvisionSystemIntegration',
  'onboarding/billing/submit/start': 'AddBillingEntity',
  'onboarding/complete/start': 'UpdateAccountInfo'
};

const OPERATION_GATED_COMMANDS = new Set( Object.keys( commandOperations ));

/**
 * Permission control - who can execute which commands.
 *
 * Source hierarchy:
 * - 'user': drives the wizard — connector selection, credential edits, billing
 *   field edits, and the three submit/start + complete/start triggers.
 * - 'api': policies dispatch the submit/success, submit/error outcomes, the account-flag
 *   sync (data/set), and billing validation errors (billing/setErrors).
 * - 'ai': blocked entirely — onboarding is a one-time privileged setup flow.
 * - 'url': not used by onboarding (no navigation/filter state).
 */
const canApply = ({
  state,
  command,
  meta
}: {
  state: OnboardingState;
  command: OnboardingCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: policies dispatch outcomes, the account-flag sync, and
  // billing validation errors.
  if ( source === 'api' ) {
    return (
      type === 'onboarding/data/set' ||
      type === 'onboarding/invoicing/submit/success' ||
      type === 'onboarding/invoicing/submit/error' ||
      type === 'onboarding/billing/setErrors' ||
      type === 'onboarding/billing/submit/success' ||
      type === 'onboarding/billing/submit/error' ||
      type === 'onboarding/complete/success' ||
      type === 'onboarding/complete/error'
    );
  }

  // User source: drives the wizard inputs and the submit/complete triggers.
  if ( source === 'user' ) {
    // Privileged "start" triggers are capability-gated against the backend
    // command their policy invokes.
    if ( OPERATION_GATED_COMMANDS.has( type )) {
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }
    return (
      type === 'onboarding/invoicing/selectConnector' ||
      type === 'onboarding/invoicing/updateCredentials' ||
      type === 'onboarding/billing/updateField'
    );
  }

  // AI source: onboarding is a one-time privileged setup flow — deny all.
  if ( source === 'ai' ) {
    return false;
  }

  // URL source: onboarding has no navigation/filter state — deny all.
  if ( source === 'url' ) {
    return false;
  }

  // Unknown sources: deny
  return false;
};

/**
 * Normalize state for consistency.
 *
 * No normalization is required for the onboarding flow: the store holds only
 * persisted-bound form inputs and submit status, with no paginated lists or
 * stale derived state to clamp. Kept as an identity stub so the shape matches
 * the account-domain invariants.
 */
const normalize = ( state: OnboardingState ): OnboardingState => {
  return state;
};

/**
 * Validate state for UI error display.
 *
 * Billing field validation is driven explicitly via `onboarding/billing/setErrors`
 * (dispatched by the submit policy reusing the account-domain billing rule), so
 * there is no implicit always-on validation here. Returns null (no errors).
 */
const validate = ( _state: OnboardingState ): Record<string, string> | null => {
  return null;
};

export const invariants: Invariants<OnboardingState, OnboardingCommand> = {
  normalize,
  canApply,
  validate
};
